import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { getCasesAsync, loadMoreCasesAsync } from "../../../slices/case";
import { useSearchParams } from "react-router-dom";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PlusIcon, Squares2X2Icon, TableCellsIcon } from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import { priorityOptions } from "../../../components/dashboard/quality/deviations/utils";
import { CasesTableV2 } from "../../../components/dashboard/cases/cases-table-v2";
import { CasesTableNewView, LIST_FIELDS } from "../../../components/dashboard/cases/CasesTableNewView";
import { getUsersAsync } from "../../../slices/user";
import { fetchCaseTypes } from "../../../api/case/type";
import CaseCreateOnboardingDialog from "../../../components/dashboard/cases/guides/CaseCreateOnboardingDialog";

const CASES_VIEW_PREFERENCE_KEY = "promang_cases_list_view";
/** URL value when no status restriction (all statuses). */
const CASE_STATUS_URL_ALL = "all";
const DEFAULT_CASE_STATUSES = ["NEW", "PROCESSING", "FINISHED"];
const FETCH_DEBOUNCE_MS = 400;

function parseCasesUrlMeta(searchParams) {
  const rawStatus = searchParams.get("status");
  let statuses;
  if (rawStatus === null || rawStatus === undefined) {
    statuses = [...DEFAULT_CASE_STATUSES];
  } else if (rawStatus === CASE_STATUS_URL_ALL) {
    statuses = [];
  } else {
    statuses = rawStatus.split(",").map((s) => s.trim()).filter(Boolean);
  }
  const overdueRaw = searchParams.get("only_overdue");
  const onlyOverdue =
    overdueRaw === null || overdueRaw === undefined
      ? true
      : overdueRaw === "1" || overdueRaw === "true";
  return { statuses, onlyOverdue };
}

function buildBackendFiltersFromModuleFilter(filters) {
  const backendFilters = [];
  const searchQuery = filters.query || "";
  const hasSearchQuery = searchQuery.trim().length > 0;

  if (filters.statuses && filters.statuses.length > 0 && !hasSearchQuery) {
    backendFilters.push({
      name: "status",
      value: JSON.stringify(filters.statuses),
    });
  }

  if (filters.users && filters.users.length > 0) {
    backendFilters.push({
      name: "user_assigned_id",
      value: JSON.stringify(filters.users),
    });
  }

  if (filters.caseTypes && filters.caseTypes.length > 0) {
    backendFilters.push({
      name: "case_type",
      value: JSON.stringify(filters.caseTypes),
    });
  }

  if (filters.priorities && filters.priorities.length > 0) {
    backendFilters.push({
      name: "priority",
      value: JSON.stringify(filters.priorities),
    });
  }

  if (filters.onlyOverdue && !hasSearchQuery) {
    backendFilters.push({
      name: "view",
      value: JSON.stringify(["ONLY_OVERDUE"]),
    });
  }

  return { backendFilters, searchQuery };
}

function createInitialController(searchString, fields) {
  const sp = new URLSearchParams(searchString);
  const meta = parseCasesUrlMeta(sp);
  const query = sp.get("query") || "";
  const { backendFilters } = buildBackendFiltersFromModuleFilter({
    query,
    statuses: meta.statuses,
    onlyOverdue: meta.onlyOverdue,
    users: [],
    caseTypes: [],
    priorities: [],
  });

  return {
    filters: backendFilters,
    meta,
    page: sp.get("page") || 1,
    per_page: sp.get("per_page") || 25,
    query,
    sort: sp.get("sort") || "desc",
    sortBy: sp.get("sortBy") || "created_at",
    view: sp.get("view") || "all",
    fields,
  };
}

export const CasesList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const caseState = useSelector((store) => {
    return store.cases;
  });

  const userState = useSelector((store) => {
    return store.users;
  });

  const { isLoading } = caseState;
  const [caseTypes, setCaseTypes] = useState([]);
  const skipFetchRef = useRef(false);
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const debouncedFetchRef = useRef(null);
  if (!debouncedFetchRef.current) {
    debouncedFetchRef.current = debounce((ctrl) => {
      if (skipFetchRef.current) {
        skipFetchRef.current = false;
        return;
      }
      dispatchRef.current(getCasesAsync(ctrl));
    }, FETCH_DEBOUNCE_MS);
  }

  const [useNewView, setUseNewView] = useState(() => {
    try {
      return localStorage.getItem(CASES_VIEW_PREFERENCE_KEY) === "new";
    } catch {
      return false;
    }
  });

  const defaultFields = useNewView
    ? LIST_FIELDS
    : "id,case_number,case_type,priority,status,created_at,updated_at,customer,user_assigned,last_item,fields,title";

  const [controller, setController] = useState(() =>
    createInitialController(
      typeof window !== "undefined" ? window.location.search.substring(1) : searchParams.toString(),
      defaultFields
    )
  );

  const handleToggleView = () => {
    const nextView = !useNewView;
    setUseNewView(nextView);
    try {
      localStorage.setItem(CASES_VIEW_PREFERENCE_KEY, nextView ? "new" : "legacy");
    } catch {
      /* ignore */
    }
    setController((prev) => ({
      ...prev,
      fields: nextView
        ? LIST_FIELDS
        : "id,case_number,case_type,priority,status,created_at,updated_at,customer,user_assigned,last_item,fields,title",
    }));
  };

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        const setOrDel = (key, val) => {
          if (val !== undefined && val !== null && String(val) !== "") {
            p.set(key, String(val));
          } else {
            p.delete(key);
          }
        };

        setOrDel("page", controller.page);
        setOrDel("per_page", controller.per_page);
        setOrDel("sort", controller.sort);
        setOrDel("sortBy", controller.sortBy);
        setOrDel("view", controller.view);

        if (controller.query && String(controller.query).trim()) {
          p.set("query", String(controller.query).trim());
        } else {
          p.delete("query");
        }

        const m = controller.meta;
        if (m) {
          const statusVal =
            m.statuses.length === 0 ? CASE_STATUS_URL_ALL : m.statuses.join(",");
          p.set("status", statusVal);
          p.set("only_overdue", m.onlyOverdue ? "1" : "0");
        }

        return p;
      },
      { replace: true }
    );
  }, [controller, setSearchParams]);

  const handleFiltersChange = (filters) => {
    skipFetchRef.current = false;
    const { backendFilters, searchQuery } = buildBackendFiltersFromModuleFilter({
      query: filters.query || "",
      statuses: filters.statuses || [],
      onlyOverdue: !!filters.onlyOverdue,
      users: filters.users || [],
      caseTypes: filters.caseTypes || [],
      priorities: filters.priorities || [],
    });

    setController((prev) => ({
      ...prev,
      page: 1,
      query: searchQuery,
      filters: backendFilters,
      meta: {
        statuses: filters.statuses || [],
        onlyOverdue: !!filters.onlyOverdue,
      },
    }));
  };

  const handlePageChange = (newPage) => {
    setController((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleLoadMore = () => {
    const nextPage = Number(controller.page) + 1;
    skipFetchRef.current = true;
    dispatch(loadMoreCasesAsync({ ...controller, page: nextPage }));
    setController((prev) => ({ ...prev, page: nextPage }));
  };

  useEffect(() => {
    return () => debouncedFetchRef.current?.cancel();
  }, []);

  useEffect(() => {
    debouncedFetchRef.current(controller);
  }, [controller]);

  useEffect(() => {
    dispatch(
      getUsersAsync({
        filters: [{ name: "role", value: "case" }],
        page: 1,
        per_page: 100,
        query: "",
        sort: "asc",
        sortBy: "name",
        view: "all",
        fields: "id,name,displayTitle",
      })
    );

    fetchCaseTypes()
      .then((response) => {
        const data = response?.data?.data || response?.data || [];
        setCaseTypes(data);
      })
      .catch((error) => {
        console.error("Error fetching case types:", error);
      });
  }, [dispatch]);

  const getInitialFilters = () => {
    const initialFilters = {
      query: controller.query || "",
      statuses: controller.meta?.statuses ?? [],
      users: [],
      caseTypes: [],
      priorities: [],
      onlyOverdue: controller.meta?.onlyOverdue ?? false,
    };

    if (controller.filters && controller.filters.length > 0) {
      controller.filters.forEach((filter) => {
        if (filter.name === "status" || filter.name === "view") {
          return;
        }
        try {
          const values = JSON.parse(filter.value);
          switch (filter.name) {
            case "user_assigned_id":
              initialFilters.users = Array.isArray(values) ? values : [values];
              break;
            case "case_type":
              initialFilters.caseTypes = Array.isArray(values) ? values : [values];
              break;
            case "priority":
              initialFilters.priorities = Array.isArray(values) ? values : [values];
              break;
            default:
              break;
          }
        } catch (e) {
          switch (filter.name) {
            case "user_assigned_id":
              initialFilters.users = [filter.value];
              break;
            case "case_type":
              initialFilters.caseTypes = [filter.value];
              break;
            case "priority":
              initialFilters.priorities = [filter.value];
              break;
            default:
              break;
          }
        }
      });
    }

    return initialFilters;
  };

  const userOptions = userState.data.map((user) => ({
    value: user.id.toString(),
    label: user.displayTitle || user.name,
  }));

  const caseTypeOptions = caseTypes.map((type) => ({
    value: type.id.toString(),
    label: type.displayTitle || type.title,
  }));

  const customDropdownFilters = [
    {
      key: "caseTypes",
      label: t("Case Types"),
      options: caseTypeOptions,
    },
    {
      key: "priorities",
      label: t("Priorities"),
      options: priorityOptions.map((priority, index) => ({
        value: (index + 1).toString(),
        label: priority.label,
      })),
    },
  ];

  const customCheckboxButtonFilters = [
    {
      key: "onlyOverdue",
      label: t("Only Overdue"),
      defaultValue: true,
    },
  ];

  const handleItemsRefresh = () => {
    // dispatch(getCasesAsync(controller));
  };

  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={[
          {
            label: useNewView ? t("Use list view") : t("Use new view"),
            action: handleToggleView,
            icon: useNewView ? TableCellsIcon : Squares2X2Icon,
            hidden: false,
            menu: false,
            "aria-label":
              useNewView
                ? t("Switch to classic list view")
                : t("Switch to compact editable table view"),
          },
        ]}
        title={"Cases"}
        meta={[]}
        mainButton={{
          label: "New",
          action: () => setCreateDialogOpen(true),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3 bg-white px-6 py-6 rounded-lg">
            <ModuleFilter
              className={"mt-10"}
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={getInitialFilters()}
              searchPlaceholder={t("Search cases (e.g., case number)...")}
              filterTitle={t("Filter Cases")}
              acceptedStatuses={["NEW", "PROCESSING", "FINISHED", "CLOSED"]}
              userOptions={userOptions}
              customDropdownFilters={customDropdownFilters}
              customCheckboxButtonFilters={customCheckboxButtonFilters}
            />
          </div>

          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            {useNewView ? (
              <CasesTableNewView
                itemState={caseState}
                itemsCount={caseState.count}
                totalCount={caseState.totalCount}
                currentCount={caseState.currentCount}
                page={controller.page}
                onPageChange={handlePageChange}
                onLoadMore={handleLoadMore}
                perPage={controller.per_page}
                onRefresh={() => dispatch(getCasesAsync(controller))}
                controller={controller}
                onControllerChange={(c) => {
                  skipFetchRef.current = false;
                  setController(c);
                }}
                modernView={true}
              />
            ) : (
              <CasesTableV2
                itemState={caseState}
                itemsCount={caseState.count}
                totalCount={caseState.totalCount}
                currentCount={caseState.currentCount}
                page={controller.page}
                onPageChange={handlePageChange}
                perPage={controller.per_page}
                onRefresh={() => handleItemsRefresh()}
              />
            )}
          </div>
        </div>
      </DashboardHeader>
      <CaseCreateOnboardingDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={() => dispatch(getCasesAsync(controller))}
      />
    </>
  );
};
